import { env } from './env';
import type { BootstrapResponse } from '../api/generated';

/** cnp-api `GET /bootstrap` (handoff §4.1). Origins/URLs are used VERBATIM — never composed. */
export type Bootstrap = Required<
  Pick<BootstrapResponse, 'tenantSlug' | 'tenantName' | 'tenantStatus' | 'loginUrl' | 'authOrigin'>
> & {
  /** The contract requires it; the SPA tolerates its absence (0001, A9 spirit). */
  deployEnv: string | null;
};

/** `status` is the HTTP status, or null for a transport failure / unreadable body. `code` is the
 *  envelope's `error.code` when present (HOSTNAME_NOT_REGISTERED, TENANT_AUTH_NOT_CONFIGURED, …). */
export class BootstrapError extends Error {
  override readonly name = 'BootstrapError';
  constructor(
    message: string,
    readonly status: number | null,
    readonly code: string | null = null,
  ) {
    super(message);
  }
}

const REQUIRED = ['tenantSlug', 'tenantName', 'tenantStatus', 'loginUrl', 'authOrigin'] as const;
let resolved: Bootstrap | null = null;

/** One unauthenticated fetch (no credentials) per page load; the result is held in memory only. */
export async function resolveBootstrap(): Promise<Bootstrap> {
  let response: Response;
  try {
    response = await fetch(`${env.apiBaseUrl}/bootstrap`, {
      credentials: 'omit',
      headers: { Accept: 'application/json' },
    });
  } catch {
    throw new BootstrapError('Could not reach the bootstrap endpoint.', null);
  }

  let body: unknown;
  try {
    body = await response.json();
  } catch {
    if (response.ok) {
      throw new BootstrapError('Bootstrap response was not valid JSON.', response.status);
    }
    body = null;
  }

  if (!response.ok) {
    throw new BootstrapError(
      `Bootstrap request failed with status ${response.status}.`,
      response.status,
      envelopeCode(body),
    );
  }

  const bootstrap = parseBootstrap(body);
  if (!bootstrap || !hasValidUrls(bootstrap)) {
    throw new BootstrapError('Bootstrap response is missing a required field.', response.status);
  }
  resolved = bootstrap;
  return bootstrap;
}

export function getBootstrap(): Bootstrap {
  if (!resolved) throw new Error('getBootstrap() called before bootstrap resolved.');
  return resolved;
}

export function __setBootstrapForTest(bootstrap: Bootstrap | null): void {
  resolved = bootstrap;
}

function envelopeCode(body: unknown): string | null {
  if (!isRecord(body) || !isRecord(body.error)) return null;
  return typeof body.error.code === 'string' ? body.error.code : null;
}

function parseBootstrap(body: unknown): Bootstrap | null {
  if (!isRecord(body)) return null;
  for (const key of REQUIRED) {
    const value = body[key];
    if (typeof value !== 'string' || value.length === 0) return null;
  }
  const b = body as Record<(typeof REQUIRED)[number], string> & Record<string, unknown>;
  return {
    tenantSlug: b.tenantSlug,
    tenantName: b.tenantName,
    tenantStatus: b.tenantStatus,
    loginUrl: b.loginUrl,
    authOrigin: b.authOrigin,
    deployEnv: typeof b.deployEnv === 'string' && b.deployEnv.length > 0 ? b.deployEnv : null,
  };
}

/** authOrigin must be a bare origin (no path, no trailing slash); both URLs http(s). */
function hasValidUrls(b: Bootstrap): boolean {
  try {
    const auth = new URL(b.authOrigin);
    const login = new URL(b.loginUrl);
    return auth.origin === b.authOrigin && isHttp(auth) && isHttp(login);
  } catch {
    return false;
  }
}

function isHttp(url: URL): boolean {
  return url.protocol === 'https:' || url.protocol === 'http:';
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
